import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";

import Button from "components/button/Button";
import { DropdownMenu } from "components/dropdown-menu/DropdownMenu";
import ToggleablePanel from "components/header/ToggleablePanel";
import ExportIcon from "components/icons/ExportIcon";
import Info from "components/icons/Info";
import KebabIcon from "components/icons/KebabIcon";
import CircleNumber from "components/products/CircleWithNumber";
import style from "components/products/getting-started.scss";
import StepCompletedText from "components/products/StepCompletedText";
import StepHeader from "components/products/StepHeader";
import Tooltip from "components/tooltip/Tooltip";
import { BmdeOnboardingSteps, OnboardingProductType } from "domain/onboardings";
import { OnboardingImage, SoftwarePackage, Versions } from "domain/softwarePackages";
import { Onboarding } from "services/onboardings/OnboardingsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { createShaChecksumMenuItem } from "utils/shaChecksumUtils";
import { useOutsideClick } from "utils/useOutsideClick";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface Props {
    stepIndex: number;
    menuExpanded: boolean;
    stepCompleted: boolean;
    stepActive: boolean;
    licenses: Versions[];
    stepClickable: boolean;
    fetchedSoftwarePackages?: SoftwarePackage[];
    saveOnboardingStep: (onboarding: Onboarding) => void;
    product: OnboardingProductType;
    stepInProgressIndex: number;
    saveStepInProgressIndex: (index: number) => void;
    fetchedOnboardingImages?: OnboardingImage[];
    withWorkflowLicense: boolean;
}

interface StepAction {
    description: string;
    label: string;
    onClick: () => void;
    variant?: "bmdeIso" | "bmdeUSBCreator" | "bdeUSBCreator";
}

interface Step {
    description: string;
    actions?: StepAction[];
    icon?: string;
    tooltip?: string;
}

const CreateBootableUsb = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [showContent, setShowContent] = React.useState<boolean>(false);
    const [closeInfoDialog, setCloseInfoDialog] = React.useState(true);
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const actionButtonRef = React.useRef(null);
    const dataType = props.product + "_" + BmdeOnboardingSteps.STEP_BOOTING_USB_DRIVE;
    const mobileImage = props.fetchedOnboardingImages?.find(
        (item) => item.manualProductName === "Blancco Mobile Diagnostics and Erasure"
    );
    const usbPackage = props.fetchedSoftwarePackages?.find((product) => product.productName === "Blancco USB Creator");
    React.useEffect(() => {
        if (props.stepInProgressIndex !== props.stepIndex) {
            setShowContent(false);
            setCloseInfoDialog(true);
        }
    }, [props.stepInProgressIndex]);
    React.useEffect(() => {
        if (props.menuExpanded && props.stepClickable) {
            setShowContent(true);
        }
    }, [props.menuExpanded]);

    const usbVersionToUrlMapping: Map<string, string> = new Map();
    props.fetchedSoftwarePackages?.map((product) => {
        if (product.productName === "Blancco USB Creator") {
            return product.versions.map((toolVersion) => {
                const version = toolVersion.version;
                if (version && toolVersion.downloadUrl) {
                    usbVersionToUrlMapping.set(version, toolVersion.downloadUrl);
                }
            });
        }
        return null;
    });

    const handleISODownload = () => {
        if (props.licenses.length === 1) {
            const link = document.createElement("a");
            const isoFileLink = props.fetchedOnboardingImages?.find((each) => each.licenseId === 30);
            link.href = isoFileLink?.manual as string;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleUSBCreatorInstall = () => {
        setExpanded((prev) => !prev);
    };

    const menuItems: JSX.Element[] = [...usbVersionToUrlMapping].map(([key, url]) => (
        <button
            className={style.versionLink}
            key={key}
            onClick={() => handleUSBCreatorDownload(url, key)}
            data-testid={testIds.navigation.userMenu.profileLink}
        >
            {key}
        </button>
    ));

    const handleUSBCreatorDownload = (licenseUrl: string, version: string) => {
        const link = document.createElement("a");
        link.href = licenseUrl;
        link.setAttribute("download", version);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const clickEvent = () => {
        if (props.stepClickable) {
            setShowContent(!showContent);
        }
    };

    const bdeUsbCreationSteps: Step[] = [
        {
            description: "",
            actions: [
                {
                    description: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.stepOne"),
                    label: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.usbCreatorButton"),
                    onClick: handleUSBCreatorInstall,
                    variant: "bdeUSBCreator",
                },
            ],
        },
        {
            description: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.stepTwo"),
        },
        {
            description: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.stepThree"),
        },
        {
            description: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.stepFour"),
        },
        {
            description: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.stepFive"),
        },
        {
            description: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.stepSix"),
            tooltip: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.infoIcon"),
        },
        {
            description: t("Onboarding.gettingStartedFlow.createBootableUsbDrive.stepSeven"),
        },
    ];

    const bmdeUsbCreationSteps: Step[] = [
        {
            description: "",
            actions: [
                {
                    description: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.downloadISO"),
                    label: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.downloadISOButton"),
                    onClick: handleISODownload,
                    variant: "bmdeIso",
                },
                {
                    description: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.downloadUSBCreator"),
                    label: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.usbCreatorButton"),
                    onClick: handleUSBCreatorInstall,
                    variant: "bmdeUSBCreator",
                },
            ],
        },
        {
            description: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.insertUSB"),
        },
        {
            description: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.bootableUSBStep"),
        },
        {
            description: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.selectUSB"),
        },
        {
            description: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.addISO"),
        },
        {
            description: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.createBootableUSB"),
            tooltip: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.createUsbTooltip"),
        },
        {
            description: t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.done"),
        },
    ];

    const handleClickOutside = () => {
        setExpanded(false);
    };

    const isUsbCard = (value?: string) => {
        return value === "bmdeUSBCreator" || "bdeUSBCreator";
    };

    const toastForShaKeys = (index: number, productName?: string, sha?: string | Versions[], variant?: string) => {
        const formattedSha = typeof sha === "string" ? sha : sha?.[index]?.sha256;
        const formattedItemTitle = isUsbCard(variant) && typeof sha !== "string" ? sha?.[index]?.version : productName;

        if (!formattedSha || !productName || !formattedItemTitle) {
            return null;
        }

        return createShaChecksumMenuItem(
            formattedSha,
            productName,
            formattedItemTitle,
            index,
            t,
            theme.iconFillColor,
            theme.whiteColor
        );
    };

    const versionUrlRef = useOutsideClick(handleClickOutside);
    function createContent() {
        return (
            <>
                <div hidden={closeInfoDialog && !showContent} className={style.stepsContainer}>
                    <div className={style.alertBox}>
                        <div>
                            <Info
                                borderColor={theme.contentBackgroundColor}
                                color={theme.sortIconActiveColor}
                                size={"18"}
                            />
                        </div>
                        <div className={style.bannerText}>
                            {props.product === OnboardingProductType.BDE
                                ? t("Onboarding.gettingStartedFlow.createBootableUsbDrive.prepareYourUSB")
                                : t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.prepareYourUSB")}
                        </div>
                    </div>
                    {(props.product === OnboardingProductType.BDE ? bdeUsbCreationSteps : bmdeUsbCreationSteps).map(
                        (step, index) => (
                            <div key={index} className={style.subStepsToFollowLayout}>
                                <CircleNumber number={index + 1} subStep={true} />
                                {!step.actions && <div className={style.stepPadding}>{step.description}</div>}
                                {step.tooltip && (
                                    <Tooltip content={step.tooltip} placement={"top"}>
                                        <span className={style.tooltipSpacing}>
                                            <Info
                                                borderColor={theme.contentBackgroundColor}
                                                color={theme.sortIconActiveColor}
                                                size={"14"}
                                            />
                                        </span>
                                    </Tooltip>
                                )}
                                {step.actions && step.actions.length > 0 && (
                                    <div className={classNames(style.columnLayout)}>
                                        {step.actions.map((action, actionIndex) => (
                                            <div
                                                key={actionIndex}
                                                className={classNames(style.actionColumn)}
                                                ref={versionUrlRef}
                                            >
                                                <div>{action.description}</div>
                                                <div className={style.stepActionButton}>
                                                    {action.label}
                                                    <div className={style.stepActionButtonWrapper}>
                                                        <div className={classNames(style.actionButton)}>
                                                            <Button
                                                                variant={"TERTIARY"}
                                                                onClick={action.onClick}
                                                                ref={actionButtonRef}
                                                                aria-expanded={expanded}
                                                            >
                                                                <ExportIcon size={24} color={theme.iconFillColor} />
                                                            </Button>
                                                        </div>
                                                        <div className={classNames(style.actionButton)}>
                                                            <ToggleablePanel
                                                                title={""}
                                                                testId={
                                                                    testIds.workArea.onboarding.step.kebabMenu.button
                                                                }
                                                                menuType={"HEADER"}
                                                                icon={
                                                                    <Tooltip
                                                                        content={t(
                                                                            "Onboarding.home.downloadIconTooltip"
                                                                        )}
                                                                    >
                                                                        <KebabIcon color={theme.iconFillColor} />
                                                                    </Tooltip>
                                                                }
                                                                hideChevron={true}
                                                            >
                                                                <DropdownMenu
                                                                    position={"AUTO"}
                                                                    testId={
                                                                        testIds.workArea.onboarding.step.kebabMenu
                                                                            .itself
                                                                    }
                                                                    menuItems={
                                                                        action.variant !== "bmdeIso"
                                                                            ? usbPackage?.versions.map(
                                                                                  (_, versionIndex) => (
                                                                                      <>
                                                                                          {toastForShaKeys(
                                                                                              versionIndex,
                                                                                              usbPackage.productName,
                                                                                              usbPackage?.versions,
                                                                                              action.variant?.toString()
                                                                                          )}
                                                                                      </>
                                                                                  )
                                                                              )
                                                                            : [
                                                                                  <>
                                                                                      {toastForShaKeys(
                                                                                          0,
                                                                                          mobileImage?.manualProductName,
                                                                                          mobileImage?.shaForManual,
                                                                                          action.variant?.toString()
                                                                                      )}
                                                                                  </>,
                                                                              ]
                                                                    }
                                                                />
                                                            </ToggleablePanel>
                                                        </div>
                                                    </div>
                                                </div>
                                                {usbVersionToUrlMapping &&
                                                    expanded &&
                                                    (action.variant === "bmdeUSBCreator" ||
                                                        action.variant === "bdeUSBCreator") && (
                                                        <div className={style.usbCreator}>{menuItems}</div>
                                                    )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    )}
                </div>
                <div className={style.markComplete} hidden={closeInfoDialog && !showContent}>
                    {props.stepCompleted ? (
                        <StepCompletedText
                            successText={t("Onboarding.gettingStartedFlow.createBootableUsbDrive.completed")}
                            testId={testIds.workArea.onboarding.step.statusLabel}
                            dataType={dataType}
                        />
                    ) : (
                        <Button
                            variant={"PRIMARY"}
                            className={classNames(buttons.primaryButtonWithoutIcon)}
                            onClick={() => {
                                props.saveStepInProgressIndex(props.stepIndex);
                                props.saveOnboardingStep({
                                    product: props.product,
                                    step: BmdeOnboardingSteps.STEP_BOOTING_USB_DRIVE,
                                });
                            }}
                            testId={testIds.workArea.onboarding.step.primaryButton}
                            data-type={dataType}
                            disabled={props.stepInProgressIndex === props.stepIndex}
                            loadingAnimation={props.stepInProgressIndex === props.stepIndex}
                        >
                            {props.stepInProgressIndex === props.stepIndex
                                ? t("Onboarding.saving")
                                : t("Onboarding.gettingStartedFlow.preConfigureBlanccoDriveErasure.markComplete")}
                        </Button>
                    )}
                </div>
            </>
        );
    }
    return (
        <StepHeader
            success={props.stepCompleted}
            showStep={showContent}
            inactive={!props.stepClickable}
            title={t("Onboarding.bmde.gettingStarted.steps.createBootableUsbDrive.title")}
            stepIndex={props.stepIndex}
            showContent={showContent}
            clickEvent={clickEvent}
            menuExpanded={props.menuExpanded}
            content={createContent()}
            type={props.product}
            stepName={BmdeOnboardingSteps.STEP_BOOTING_USB_DRIVE}
        />
    );
};

export default connector(CreateBootableUsb);
